import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  User,
  EnvelopeSimple,
  Phone,
  Briefcase,
  Calendar,
  Gear,
  Bell,
  ShieldCheck,
  Buildings,
  PencilSimple,
  Clock,
  MapPin,
  CreditCard,
  Receipt,
  TrendUp,
  TrendDown,
  UserCircle,
  Trash,
  UsersThree,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { DillAvatar, DillButton, DillToggle } from '../../../general/Dill';
import { useEffect, useMemo, useState } from 'react';
import {
  Buyer,
  BuyerUserPermissions,
  DEFAULT_BUYER_USER_PERMISSIONS,
  capitalizeWordsInSentence,
  DocumentsSummary,
  MainBuyer,
} from '@dill/dill-shared';
import EditProfileModal from '../EditProfileModal';
import BuyerInviteUserModal from '../BuyerInviteUserModal/BuyerInviteUserModal';
import {
  getBuyerAndMainBuyerDetails,
  deleteBuyerUser,
  updateBuyerUserPermissions,
} from '../../../../redux/services/buyersService';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { updateCurrentUserBuyerPermissions } from '../../../../redux/globalSlices/buyersSlice';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { openDillToast } from '../../../../utils/helpers';

// Helper Components - defined before main component
const InfoItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) => (
  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-gray-50 hover:border-gray-200 transition-colors">
    <span className="mt-0.5 flex-shrink-0 text-gray-400">{icon}</span>
    <div>
      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-xs font-medium text-gray-900 mt-0.5">{value || 'Not provided'}</p>
    </div>
  </div>
);

const PillsList = ({
  items,
  label,
  icon,
  emptyMessage = 'None added',
}: {
  items?: string[] | null;
  label: string;
  icon: React.ReactNode;
  emptyMessage?: string;
}) => (
  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-gray-50 hover:border-gray-200 transition-colors">
    <span className="mt-0.5 flex-shrink-0 text-gray-400">{icon}</span>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items && items.length > 0 ? (
          items.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-500 border border-blue-100">
              {item}
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-400">{emptyMessage}</span>
        )}
      </div>
    </div>
  </div>
);

const StatusItem = ({
  icon,
  label,
  isActive,
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}) => (
  <div className="flex items-center justify-between p-2.5 rounded-lg border border-gray-50 hover:border-gray-200 transition-colors">
    <div className="flex items-center gap-2.5">
      <span className={isActive ? 'text-emerald-500' : 'text-gray-400'}>{icon}</span>
      <span className="text-xs font-medium text-gray-700">{label}</span>
    </div>
    <span
      className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${
        isActive
          ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
          : 'bg-gray-100 text-gray-400 border-gray-200'
      }`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  </div>
);

const QuickActionButton = ({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all hover:bg-gray-100"
    style={{ color: COLORS.TEXT_PRIMARY }}>
    <div style={{ color: COLORS.PRIMARY_500 }}>{icon}</div>
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const SummaryItem = ({
  icon,
  label,
  value,
  isCurrency = true,
  trend,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: number | null;
  isCurrency?: boolean;
  trend?: 'up' | 'down' | 'neutral';
}) => {
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTrendColor = () => {
    if (trend === 'up') return COLORS.SUCCESS_500;
    if (trend === 'down') return COLORS.ERROR_500;
    return COLORS.TEXT_PRIMARY;
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-2">
        {icon && <div style={{ color: COLORS.TEXT_SECONDARY }}>{icon}</div>}
        <span className="text-sm" style={{ color: COLORS.TEXT_SECONDARY }}>
          {label}
        </span>
      </div>
      <span
        className="text-sm font-semibold"
        style={{ color: trend ? getTrendColor() : COLORS.TEXT_PRIMARY }}>
        {isCurrency ? formatCurrency(value) : value?.toLocaleString() || 'N/A'}
      </span>
    </div>
  );
};

const StatCard = ({
  icon,
  label,
  value,
  subValue,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  color: string;
}) => (
  <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: `${color}10` }}>
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: `${color}20` }}>
      <div style={{ color }}>{icon}</div>
    </div>
    <div className="flex-1">
      <p className="text-xs font-medium" style={{ color: COLORS.TEXT_SECONDARY }}>
        {label}
      </p>
      <p className="text-xl font-bold" style={{ color: COLORS.TEXT_PRIMARY }}>
        {value}
      </p>
      {subValue && (
        <p className="text-xs" style={{ color: COLORS.TEXT_TERTIARY }}>
          {subValue}
        </p>
      )}
    </div>
  </div>
);

// Utility functions
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Main Component
const MainBuyerUserProfile = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview');
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isBuyerInviteUserModalOpen, setIsBuyerInviteUserModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [isLoadingBuyerDetails, setIsLoadingBuyerDetails] = useState(false);
  const [buyerDetails, setBuyerDetails] = useState<{
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      firstName: string;
      lastName: string;
      userId: string;
      isInvitedUser: boolean;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
      permissions: BuyerUserPermissions;
    }[];
    documentsSummary: DocumentsSummary;
  } | null>(null);

  const [selectedMainBuyerId] = useParamMainBuyerId();
  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`;
  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  // Format date for display
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: User },
    // { id: 'settings' as const, label: 'Settings', icon: Gear },
  ];

  const selectedBuyer = useMemo(() => {
    const buyer = user?.userBuyers?.find(
      (buy) => selectedMainBuyer && selectedMainBuyer.buyerId === buy.id
    );

    return buyer;
  }, [selectedMainBuyer, user?.userBuyers]);

  const handleGetBuyerDetails = async () => {
    if (!selectedMainBuyerId) {
      return;
    }
    setIsLoadingBuyerDetails(true);
    setBuyerDetails(null);
    const res = await dispatch(getBuyerAndMainBuyerDetails({ mainBuyerId: selectedMainBuyerId }));
    if (res.type === 'buyers/getBuyerAndMainBuyerDetails/fulfilled') {
      const payload: any = res.payload;
      if (payload.data.buyerDetails && selectedMainBuyerId === payload.data.mainBuyerDetails.id) {
        setBuyerDetails(payload.data.buyerDetails);
        // Update the global buyer permissions in Redux
        const myPermissions: BuyerUserPermissions =
          payload.data.buyerDetails.usersEmailsDetails?.find((u: any) => u.userId === user?.id)
            ?.permissions || DEFAULT_BUYER_USER_PERMISSIONS;
        dispatch(updateCurrentUserBuyerPermissions(myPermissions));
      }
    }
    setIsLoadingBuyerDetails(false);
  };

  const currentPermissions: BuyerUserPermissions = useMemo(() => {
    const found = buyerDetails?.usersEmailsDetails.find((u) => u.userId === user?.id);
    return found?.permissions || DEFAULT_BUYER_USER_PERMISSIONS;
  }, [buyerDetails, user?.id]);

  const isCurrentUserAdmin = currentPermissions.includes('PAYER');

  const handleUpdateUserPermissions = async (userId: string, permissions: BuyerUserPermissions) => {
    if (!selectedMainBuyer) return;
    setUpdatingUserId(userId);
    const res = await dispatch(
      updateBuyerUserPermissions({ mainBuyerId: selectedMainBuyer.id, userId, permissions })
    );
    setUpdatingUserId(null);
    if (res.type === 'buyers/updateBuyerUserPermissions/fulfilled') {
      handleGetBuyerDetails();
      openDillToast({ type: 'SUCCESS', message: 'Permissions successfuly updated' });
    } else {
      const errorMsg = (res.payload as string) || 'Failed to update permissions';
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!selectedMainBuyer) return;
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Removing user',
        subTitle: 'Removing user from your team...',
      })
    );
    const res = await dispatch(deleteBuyerUser({ mainBuyerId: selectedMainBuyer.id, userId }));
    if (res.type === 'buyers/deleteBuyerUser/fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'User Removed',
          subTitle: 'The user has been removed from your team',
        })
      );
      handleGetBuyerDetails();
    } else {
      const errorMsg = (res.payload as string) || 'Failed to remove user';
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  useEffect(() => {
    if (selectedMainBuyerId) {
      handleGetBuyerDetails();
    }
  }, [selectedMainBuyerId]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Profile Header with Gradient Background */}
      <div
        className="relative h-48"
        style={{
          background: `linear-gradient(135deg, ${COLORS.PRIMARY_600} 0%, ${COLORS.PRIMARY_800} 100%)`,
        }}>
        {/* Decorative circles */}
        <div
          className="absolute top-4 right-20 w-32 h-32 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
        <div
          className="absolute bottom-8 right-40 w-20 h-20 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
        <div
          className="absolute top-12 right-60 w-12 h-12 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
      </div>

      {/* Profile Content Container */}
      <div className="max-w-6xl mx-auto px-6 -mt-24">
        {/* Profile Card */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Profile Header Section */}
          <div className="px-8 pt-6 pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
              {/* Avatar */}
              <div
                className="relative flex items-center justify-center w-32 h-32 rounded-2xl text-white text-4xl font-semibold shadow-xl ring-4 ring-white"
                style={{ backgroundColor: COLORS.PRIMARY_500 }}>
                {userInitials || <User size={48} weight="thin" />}
                {/* Status indicator */}
                <div
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white"
                  style={{ backgroundColor: COLORS.SUCCESS_500 }}
                />
              </div>

              {/* User Info */}
              <div className="flex-1  z-20  ">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-white">
                    {fullName ? capitalizeWordsInSentence(fullName) : 'User Profile'}
                  </h1>
                </div>
                <p className="text-sm mb-3" style={{ color: COLORS.TEXT_SECONDARY }}>
                  {user?.title || 'Team Member'}
                </p>
                <div
                  className="flex flex-wrap gap-4 text-sm"
                  style={{ color: COLORS.TEXT_SECONDARY }}>
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple
                      size={16}
                      weight="duotone"
                      style={{ color: COLORS.PRIMARY_500 }}
                    />
                    <span>{user?.email || 'No email'}</span>
                  </div>
                </div>
              </div>

              {/* Edit Profile Button */}
              <DillButton
                label="Edit Profile"
                icon={<PencilSimple size={18} weight="bold" />}
                variant="secondary"
                onClick={() => setIsEditProfileModalOpen(true)}
              />
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b px-8" style={{ borderColor: COLORS.GREY_200 }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium transition-all border-b-2 -mb-px ${
                  activeTab === tab.id ? '' : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  color: activeTab === tab.id ? COLORS.PRIMARY_600 : COLORS.TEXT_SECONDARY,
                  borderColor: activeTab === tab.id ? COLORS.PRIMARY_600 : 'transparent',
                }}>
                <tab.icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6 pb-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <User size={15} weight="duotone" className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">User Information</h3>
                  <p className="text-xs text-gray-400">Personal details and contact info</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoItem
                  icon={<UserCircle size={18} weight="duotone" />}
                  label="First Name"
                  value={user?.firstName ? capitalizeWordsInSentence(user?.firstName) : ''}
                />
                <InfoItem
                  icon={<UserCircle size={18} weight="duotone" />}
                  label="Last Name"
                  value={user?.lastName ? capitalizeWordsInSentence(user?.lastName) : ''}
                />{' '}
                <InfoItem
                  icon={<Briefcase size={18} weight="duotone" />}
                  label="Job Title"
                  value={user?.title}
                />
                <InfoItem
                  icon={<EnvelopeSimple size={18} weight="duotone" />}
                  label="Email Address"
                  value={user?.email}
                />
                <InfoItem
                  icon={<Phone size={18} weight="duotone" />}
                  label="Phone Number"
                  value={user?.phoneNumber}
                />
              </div>
            </div>

            {/* Company Information Card */}
            {(selectedMainBuyer || selectedBuyer) && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Buildings size={15} weight="duotone" className="text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Company Information</h3>
                      <p className="text-xs text-gray-400">{selectedMainBuyer?.name}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoItem
                    icon={<Buildings size={18} weight="duotone" />}
                    label="Company Name"
                    value={selectedMainBuyer?.name}
                  />
                  {selectedBuyer && (
                    <>
                      <InfoItem
                        icon={<MapPin size={18} weight="duotone" />}
                        label="Address"
                        value={
                          selectedBuyer.address
                            ? `${selectedBuyer.address}${
                                selectedBuyer.city ? `, ${selectedBuyer.city}` : ''
                              }${selectedBuyer.state ? `, ${selectedBuyer.state}` : ''}${
                                selectedBuyer.zipCode ? ` ${selectedBuyer.zipCode}` : ''
                              }`
                            : null
                        }
                      />
                    </>
                  )}
                  {selectedBuyer && (
                    <div className="col-span-1 sm:col-span-2 space-y-4">
                      <PillsList
                        icon={<Phone size={18} weight="duotone" />}
                        label="Company Phone Numbers"
                        items={selectedBuyer.phoneNumbers}
                        emptyMessage="No phone numbers added"
                      />
                    </div>
                  )}
                </div>

                {/* Team Members Section/ Account Access */}
                {isLoadingBuyerDetails && (
                  <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-center py-8">
                    <span className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {buyerDetails && !isLoadingBuyerDetails && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <UsersThree size={15} weight="duotone" className="text-blue-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900">Account Access</h3>
                          <p className="text-xs text-gray-400">
                            {buyerDetails.usersEmailsDetails.length} member
                            {buyerDetails.usersEmailsDetails.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      {isCurrentUserAdmin && (
                        <DillButton
                          label="Invite Users"
                          icon={<User size={20} />}
                          variant="secondary"
                          height="30px"
                          onClick={() => setIsBuyerInviteUserModalOpen(true)}
                        />
                      )}
                    </div>
                    <div className="space-y-3">
                      {buyerDetails ? (
                        <div>
                          {/* Permission descriptions - mobile only */}
                          <div className="flex flex-col gap-1.5 mb-3 sm:hidden">
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50">
                              <span className="text-[10px] font-semibold text-gray-500 uppercase">
                                Viewer
                              </span>
                              <span className="text-[10px] text-gray-400">
                                — Can retrieve invoices &amp; statements
                              </span>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50">
                              <span className="text-[10px] font-semibold text-gray-500 uppercase">
                                Payer
                              </span>
                              <span className="text-[10px] text-gray-400">— Can pay online</span>
                            </div>
                          </div>
                          {/* Column headers - hidden on mobile */}
                          <div className="hidden sm:grid grid-cols-[1fr_160px_120px_28px] gap-2 items-center px-3 pb-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Users
                            </span>
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Viewer
                              </span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                Can retrieve invoices &amp; statements
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Payer
                              </span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                Can pay online
                              </span>
                            </div>
                            <span />
                          </div>
                          {buyerDetails.usersEmailsDetails.map((userDetail, index) => {
                            const isSelf = userDetail.userId === user?.id;
                            const permissions =
                              userDetail.permissions || DEFAULT_BUYER_USER_PERMISSIONS;
                            const hasPayer = permissions.includes('PAYER');
                            const isLoading = updatingUserId === userDetail.userId;
                            const canToggle = isCurrentUserAdmin && !isSelf;
                            return (
                              <div
                                key={userDetail.userId}
                                className="flex flex-col gap-3 sm:grid sm:grid-cols-[1fr_160px_120px_28px] sm:gap-2 sm:items-center p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <DillAvatar
                                    name={userDetail.firstName || userDetail.email}
                                    idx={index}
                                  />
                                  <div className="min-w-0 flex-1">
                                    {userDetail.firstName || userDetail.lastName ? (
                                      <div className="flex items-center gap-1.5">
                                        <p className="text-xs font-medium text-gray-900 truncate capitalize">
                                          {`${userDetail.firstName} ${userDetail.lastName}`.trim()}
                                        </p>
                                        {isSelf && (
                                          <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                            You
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1.5">
                                        <p className="text-xs font-medium text-gray-900 truncate">
                                          {userDetail.email}
                                        </p>
                                        {isSelf && (
                                          <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                            You
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {(userDetail.firstName || userDetail.lastName) && (
                                      <p className="text-[11px] text-gray-400 truncate">
                                        {userDetail.email}
                                      </p>
                                    )}
                                    <p className="text-[11px] text-gray-400">
                                      {userDetail.isInvitedUser ? 'Invited' : 'Active'}
                                    </p>
                                  </div>
                                  {/* Delete button inline on mobile */}
                                  <div className="flex items-center sm:hidden">
                                    <DillButton
                                      variant="icon-red"
                                      icon={<Trash size={16} weight="bold" />}
                                      isDisabled={!isCurrentUserAdmin || isSelf}
                                      onClick={() =>
                                        dispatch(
                                          openMessageModal({
                                            buttonText: 'Cancel',
                                            isSecondButton: true,
                                            secondButtonText: 'Yes, remove',
                                            modalType: 'WARN',
                                            title: 'Remove Team Member',
                                            subTitle: `Are you sure you want to remove ${
                                              userDetail.email || 'this user'
                                            } from your team?`,
                                            onSecondButtonClick: () => {
                                              handleDeleteUser(userDetail.userId);
                                            },
                                          })
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                                {/* Toggles row - horizontal on mobile with labels */}
                                <div className="flex items-center gap-4 pl-10 sm:hidden">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-gray-500 uppercase">
                                      Viewer
                                    </span>
                                    <DillToggle value isDisabled onChange={() => {}} />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-gray-500 uppercase">
                                      Payer
                                    </span>
                                    {isLoading ? (
                                      <span className="w-3.5 h-3.5 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <DillToggle
                                        value={hasPayer}
                                        isDisabled={!canToggle}
                                        onChange={(v) => {
                                          handleUpdateUserPermissions(
                                            userDetail.userId,
                                            v ? ['VIEWER', 'PAYER'] : ['VIEWER']
                                          );
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                                {/* Desktop toggles and delete - hidden on mobile */}
                                <div className="hidden sm:flex items-center">
                                  <DillToggle value isDisabled onChange={() => {}} />
                                </div>
                                <div className="hidden sm:flex items-center">
                                  {isLoading ? (
                                    <span className="w-3.5 h-3.5 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <DillToggle
                                      value={hasPayer}
                                      isDisabled={!canToggle}
                                      onChange={(v) => {
                                        handleUpdateUserPermissions(
                                          userDetail.userId,
                                          v ? ['VIEWER', 'PAYER'] : ['VIEWER']
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="hidden sm:flex items-center justify-center">
                                  <DillButton
                                    variant="icon-red"
                                    icon={<Trash size={16} weight="bold" />}
                                    isDisabled={!isCurrentUserAdmin || isSelf}
                                    onClick={() =>
                                      dispatch(
                                        openMessageModal({
                                          buttonText: 'Cancel',
                                          isSecondButton: true,
                                          secondButtonText: 'Yes, remove',
                                          modalType: 'WARN',
                                          title: 'Remove Team Member',
                                          subTitle: `Are you sure you want to remove ${
                                            userDetail.email || 'this user'
                                          } from your team?`,
                                          onSecondButtonClick: () => {
                                            handleDeleteUser(userDetail.userId);
                                          },
                                        })
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-8 text-center text-sm text-gray-400">
                          Loading team members...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Account Status Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={15} weight="duotone" className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Account Status</h3>
                </div>
              </div>
              <div className="space-y-2">
                <StatusItem
                  icon={<ShieldCheck size={18} weight="duotone" />}
                  label="Account Verified"
                  isActive={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={isEditProfileModalOpen}
        handleClose={() => {
          setIsEditProfileModalOpen(false);
        }}
      />

      {/* Buyer Invite User Modal */}
      <BuyerInviteUserModal
        open={isBuyerInviteUserModalOpen}
        handleClose={() => {
          setIsBuyerInviteUserModalOpen(false);
          handleGetBuyerDetails();
        }}
        mainBuyer={selectedMainBuyer}
      />
    </div>
  );
};

export default MainBuyerUserProfile;
